import { supabase } from './supabase';
import { Employee } from '../types';
import {
  calculateSSSContribution,
  calculatePhilHealthContribution,
  calculatePagIBIGContribution,
  calculateMonthlyWithholdingTax
} from '../utils/philippinePayrollUtils';

export interface GovernmentDeduction {
  employee_id: string;
  employee_name: string;
  employee_number?: string;
  sss_number?: string;
  philhealth_number?: string;
  pagibig_number?: string;
  tin_number?: string;
  salary: number;
  sss_contribution: number;
  philhealth_contribution: number;
  pagibig_contribution: number;
  withholding_tax: number;
  total_deductions: number;
  deduct_sss: boolean;
  deduct_philhealth: boolean;
  deduct_pagibig: boolean;
  deduct_tax: boolean;
}

export interface GovernmentDeductionSummary {
  period: string;
  total_employees: number;
  total_sss: number;
  total_philhealth: number;
  total_pagibig: number;
  total_tax: number;
  total_all_deductions: number;
  deductions: GovernmentDeduction[];
}

class GovernmentDeductionsService {
  /**
   * Get all active employees with their government deduction calculations
   */
  async getEmployeesWithDeductions(): Promise<GovernmentDeduction[]> {
    try {
      const { data: employees, error } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .order('last_name', { ascending: true });

      if (error) {
        console.error('Error fetching employees:', error);
        throw error;
      }

      if (!employees || employees.length === 0) {
        return [];
      }

      return employees.map(employee => this.calculateEmployeeDeductions(employee));
    } catch (error) {
      console.error('Error in getEmployeesWithDeductions:', error);
      throw error;
    }
  }

  /**
   * Calculate government deductions for a single employee
   */
  calculateEmployeeDeductions(employee: Employee): GovernmentDeduction {
    const sssContribution = (employee.deduct_sss !== false) 
      ? calculateSSSContribution(employee.salary) 
      : 0;
    
    const philhealthContribution = (employee.deduct_philhealth !== false) 
      ? calculatePhilHealthContribution(employee.salary) 
      : 0;
    
    const pagibigContribution = (employee.deduct_pagibig !== false) 
      ? calculatePagIBIGContribution(employee.salary) 
      : 0;
    
    const withholdingTax = (employee.deduct_tax !== false) 
      ? calculateMonthlyWithholdingTax(employee.salary) 
      : 0;

    const totalDeductions = sssContribution + philhealthContribution + pagibigContribution + withholdingTax;

    return {
      employee_id: employee.id,
      employee_name: `${employee.first_name} ${employee.last_name}`,
      employee_number: employee.id.slice(-6).toUpperCase(), // Use last 6 chars of ID as employee number
      sss_number: employee.sss_number,
      philhealth_number: employee.philhealth_number,
      pagibig_number: employee.pagibig_number,
      tin_number: employee.tin_number,
      salary: employee.salary,
      sss_contribution: sssContribution,
      philhealth_contribution: philhealthContribution,
      pagibig_contribution: pagibigContribution,
      withholding_tax: withholdingTax,
      total_deductions: totalDeductions,
      deduct_sss: employee.deduct_sss !== false,
      deduct_philhealth: employee.deduct_philhealth !== false,
      deduct_pagibig: employee.deduct_pagibig !== false,
      deduct_tax: employee.deduct_tax !== false
    };
  }

  /**
   * Get government deductions summary for a specific period
   */
  async getDeductionsSummary(period?: string): Promise<GovernmentDeductionSummary> {
    try {
      const deductions = await this.getEmployeesWithDeductions();
      const currentPeriod = period || new Date().toISOString().slice(0, 7); // YYYY-MM format

      const summary = deductions.reduce(
        (acc, deduction) => {
          acc.total_sss += deduction.sss_contribution;
          acc.total_philhealth += deduction.philhealth_contribution;
          acc.total_pagibig += deduction.pagibig_contribution;
          acc.total_tax += deduction.withholding_tax;
          acc.total_all_deductions += deduction.total_deductions;
          return acc;
        },
        {
          period: currentPeriod,
          total_employees: deductions.length,
          total_sss: 0,
          total_philhealth: 0,
          total_pagibig: 0,
          total_tax: 0,
          total_all_deductions: 0,
          deductions
        }
      );

      // Round all totals to 2 decimal places
      summary.total_sss = Math.round(summary.total_sss * 100) / 100;
      summary.total_philhealth = Math.round(summary.total_philhealth * 100) / 100;
      summary.total_pagibig = Math.round(summary.total_pagibig * 100) / 100;
      summary.total_tax = Math.round(summary.total_tax * 100) / 100;
      summary.total_all_deductions = Math.round(summary.total_all_deductions * 100) / 100;

      return summary;
    } catch (error) {
      console.error('Error in getDeductionsSummary:', error);
      throw error;
    }
  }

  /**
   * Get SSS deductions only
   */
  async getSSSDeductions(): Promise<GovernmentDeduction[]> {
    const deductions = await this.getEmployeesWithDeductions();
    return deductions.filter(d => d.deduct_sss && d.sss_contribution > 0);
  }

  /**
   * Get PhilHealth deductions only
   */
  async getPhilHealthDeductions(): Promise<GovernmentDeduction[]> {
    const deductions = await this.getEmployeesWithDeductions();
    return deductions.filter(d => d.deduct_philhealth && d.philhealth_contribution > 0);
  }

  /**
   * Get Pag-IBIG deductions only
   */
  async getPagIBIGDeductions(): Promise<GovernmentDeduction[]> {
    const deductions = await this.getEmployeesWithDeductions();
    return deductions.filter(d => d.deduct_pagibig && d.pagibig_contribution > 0);
  }

  /**
   * Get Tax deductions only
   */
  async getTaxDeductions(): Promise<GovernmentDeduction[]> {
    const deductions = await this.getEmployeesWithDeductions();
    return deductions.filter(d => d.deduct_tax && d.withholding_tax > 0);
  }

  /**
   * Get deductions by employee ID
   */
  async getEmployeeDeductions(employeeId: string): Promise<GovernmentDeduction | null> {
    try {
      const { data: employee, error } = await supabase
        .from('employees')
        .select('*')
        .eq('id', employeeId)
        .eq('status', 'active')
        .single();

      if (error || !employee) {
        console.error('Error fetching employee:', error);
        return null;
      }

      return this.calculateEmployeeDeductions(employee);
    } catch (error) {
      console.error('Error in getEmployeeDeductions:', error);
      return null;
    }
  }
}

export const governmentDeductionsService = new GovernmentDeductionsService();